import { Controller } from "@hotwired/stimulus"
import { TimingRenderer } from "../lib/timing_renderer"
import {
  TimingState,
  NotePayload,
  createInitialState,
  addNote,
  deleteSelected,
  snapBeat,
  undo,
  pushUndo,
  selectInRect,
} from "../lib/timing_state"

export default class extends Controller {
  static targets = [
    "canvas",
    "noteCount",
    "unsavedIndicator",
    "snapSelect",
    "zoomSlider",
    "trackSelect",
    "playBtn",
  ]
  static values = {
    lessonId: String,
    trackId: String,
    notes: Array,
    mapping: Object,
    bpm: Number,
    timeSig: String,
    duration: Number,
    instrument: String,
  }

  declare canvasTarget: HTMLCanvasElement
  declare noteCountTarget: HTMLElement
  declare unsavedIndicatorTarget: HTMLElement
  declare snapSelectTarget: HTMLSelectElement
  declare zoomSliderTarget: HTMLInputElement
  declare trackSelectTarget: HTMLSelectElement
  declare playBtnTarget: HTMLElement
  declare lessonIdValue: string
  declare trackIdValue: string
  declare notesValue: NotePayload[]
  declare mappingValue: Record<string, number>
  declare bpmValue: number
  declare timeSigValue: string
  declare durationValue: number
  declare instrumentValue: string

  private renderer!: TimingRenderer
  private state!: TimingState
  private shiftHeld = false

  // Interaction modes
  private interaction: "none" | "pan" | "marquee" | "drag-note" | "resize-note" = "none"
  private panStartX = 0
  private panStartBeat = 0
  private marqueeStartX = 0
  private marqueeStartY = 0
  private dragNoteIdx = -1
  private dragStartMouseX = 0
  private dragStartBeat = 0
  private dragStartLane = ""
  private dragOrigBeats: number[] = []
  private dragOrigLanes: string[] = []  // original beats of all selected notes at drag start

  // Playback
  private playing = false
  private playStartWallTime = 0
  private playStartBeat = 1
  private rafId: number | null = null
  private audioCtx: AudioContext | null = null
  private noteEvents: { beat: number; endBeat: number; note: string; midi: number }[] = []
  private scheduledUntil = 0

  private boundKeyDown: ((e: KeyboardEvent) => void) | null = null
  private boundKeyUp: ((e: KeyboardEvent) => void) | null = null

  connect(): void {
    const [top, bottom] = this.timeSigValue.split("/").map(Number)

    this.state = createInitialState(
      this.notesValue,
      this.mappingValue,
      this.bpmValue,
      top || 4,
      bottom || 4,
      this.durationValue
    )

    this.renderer = new TimingRenderer({
      canvas: this.canvasTarget,
      instrument: this.instrumentValue,
    })

    this.bindEvents()
    this.updateNoteCount()
    this.render()
  }

  disconnect(): void {
    this.stopPlayback()
    this.renderer?.destroy()
    if (this.boundKeyDown) { document.removeEventListener("keydown", this.boundKeyDown); this.boundKeyDown = null }
    if (this.boundKeyUp) { document.removeEventListener("keyup", this.boundKeyUp); this.boundKeyUp = null }
  }

  private bindEvents(): void {
    const c = this.canvasTarget
    c.addEventListener("mousedown", (e) => this.onMouseDown(e))
    c.addEventListener("mousemove", (e) => this.onMouseMove(e))
    c.addEventListener("mouseup", (e) => this.onMouseUp(e))
    c.addEventListener("mouseleave", () => this.onMouseLeave())
    c.addEventListener("dblclick", (e) => this.onDoubleClick(e))
    c.addEventListener("wheel", (e) => this.onWheel(e), { passive: false })
    c.addEventListener("auxclick", (e) => { if (e.button === 1) e.preventDefault() })

    this.boundKeyDown = (e) => this.onKeyDown(e)
    this.boundKeyUp = (e) => this.onKeyUp(e)
    document.addEventListener("keydown", this.boundKeyDown)
    document.addEventListener("keyup", this.boundKeyUp)

    new ResizeObserver(() => { this.renderer.resize(); this.render() }).observe(c.parentElement!)
  }

  private canvasPos(e: MouseEvent): { x: number; y: number } {
    const r = this.canvasTarget.getBoundingClientRect()
    return { x: e.clientX - r.left, y: e.clientY - r.top }
  }

  private canvasSize(): { w: number; h: number } {
    return {
      w: this.canvasTarget.width / (window.devicePixelRatio || 1),
      h: this.canvasTarget.height / (window.devicePixelRatio || 1),
    }
  }

  private playheadX(w: number): number {
    return 64 + (w - 64) * 0.24
  }

  private pxPerBeat(w: number): number {
    return (w - this.playheadX(w)) / this.renderer.getLookAhead()
  }

  // ── Mouse ──

  private onMouseDown(e: MouseEvent): void {
    const { x, y } = this.canvasPos(e)
    const { w, h } = this.canvasSize()

    // Middle-click → pan (always)
    if (e.button === 1) {
      e.preventDefault()
      this.startPan(x)
      return
    }

    // Check note hit BEFORE shift check
    const noteIdx = this.renderer.hitTest(x, y, this.state, w, h)

    // Shift+click on note → toggle in selection (accumulate)
    if (e.shiftKey && noteIdx >= 0) {
      if (this.state.selectedIndices.has(noteIdx)) {
        this.state.selectedIndices.delete(noteIdx)
      } else {
        this.state.selectedIndices.add(noteIdx)
      }
      this.render()
      return
    }

    // Shift+click on empty → pan
    if (e.shiftKey) {
      this.startPan(x)
      return
    }

    // Check resize edge on single selection
    if (this.state.selectedIndices.size === 1) {
      const selIdx = [...this.state.selectedIndices][0]
      const edgeIdx = this.renderer.hitTestEdge(x, y, this.state, w, h)
      if (edgeIdx === selIdx) {
        this.interaction = "resize-note"
        this.dragNoteIdx = selIdx
        pushUndo(this.state)
        return
      }
    }

    if (noteIdx >= 0) {
      // Click on note that's already in a multi-selection → drag the whole group
      if (this.state.selectedIndices.has(noteIdx) && this.state.selectedIndices.size > 1) {
        this.interaction = "drag-note"
        this.dragNoteIdx = noteIdx
        this.dragStartMouseX = x
        this.dragStartBeat = this.state.notes[noteIdx].beat
        this.dragStartLane = this.state.notes[noteIdx].note
        this.dragOrigBeats = [...this.state.selectedIndices].map(i => this.state.notes[i].beat)
        this.dragOrigLanes = [...this.state.selectedIndices].map(i => this.state.notes[i].note)
        pushUndo(this.state)
        this.render()
        return
      }

      // Normal click on note → select it alone, start drag
      this.state.selectedIndices.clear()
      this.state.selectedIndices.add(noteIdx)
      this.interaction = "drag-note"
      this.dragNoteIdx = noteIdx
      this.dragStartMouseX = x
      this.dragStartBeat = this.state.notes[noteIdx].beat
      this.dragStartLane = this.state.notes[noteIdx].note
      this.dragOrigBeats = [this.state.notes[noteIdx].beat]
      this.dragOrigLanes = [this.state.notes[noteIdx].note]
      pushUndo(this.state)
      this.render()
      return
    }

    // Click on empty space → clear selection, start marquee
    this.state.selectedIndices.clear()
    this.interaction = "marquee"
    this.marqueeStartX = x
    this.marqueeStartY = y
    this.render()
  }

  private startPan(mouseX: number): void {
    this.interaction = "pan"
    this.panStartX = mouseX
    this.panStartBeat = this.renderer.getBeat()
    this.canvasTarget.style.cursor = "grabbing"
  }

  private onMouseMove(e: MouseEvent): void {
    const { x, y } = this.canvasPos(e)
    const { w, h } = this.canvasSize()

    if (this.interaction === "pan") {
      const ppb = this.pxPerBeat(w)
      this.renderer.setBeat(this.panStartBeat + (this.panStartX - x) / ppb)
      this.render()
      return
    }

    if (this.interaction === "marquee") {
      this.renderer.marqueeRect = { x1: this.marqueeStartX, y1: this.marqueeStartY, x2: x, y2: y }
      selectInRect(
        this.state,
        this.marqueeStartX, this.marqueeStartY, x, y,
        w, h,
        (beat) => this.renderer["beatToX"](beat, this.playheadX(w), this.pxPerBeat(w)),
        this.renderer.getLookAhead()
      )
      this.render()
      return
    }

    if (this.interaction === "resize-note" && this.dragNoteIdx >= 0) {
      const ppb = this.pxPerBeat(w)
      const note = this.state.notes[this.dragNoteIdx]
      const noteX = this.renderer["beatToX"](note.beat, this.playheadX(w), ppb)
      note.duration = Math.max(0.125, parseFloat(((x - noteX) / ppb).toFixed(4)))
      this.state.dirty = true
      this.render()
      return
    }

    if (this.interaction === "drag-note" && this.dragNoteIdx >= 0) {
      const ppb = this.pxPerBeat(w)
      const beatDelta = (x - this.dragStartMouseX) / ppb
      const newBeat = snapBeat(this.dragStartBeat + beatDelta, this.state.snap)
      const beatShift = Math.max(0.5, newBeat) - this.dragStartBeat

      // Lane delta: compare original lane of anchor to current mouse lane
      const origAnchorLaneIdx = this.state.lanes.indexOf(this.dragStartLane)
      const currentLane = this.renderer.laneAtY(y, this.state, h)
      const currentLaneIdx = this.state.lanes.indexOf(currentLane)
      const laneDelta = currentLaneIdx - origAnchorLaneIdx

      const selectedArr = [...this.state.selectedIndices]
      for (let j = 0; j < selectedArr.length; j++) {
        const idx = selectedArr[j]
        const n = this.state.notes[idx]
        n.beat = Math.max(0.5, this.dragOrigBeats[j] + beatShift)
        // Apply lane delta, clamped to valid range
        const origIdx = this.state.lanes.indexOf(this.dragOrigLanes[j])
        const newIdx = Math.max(0, Math.min(this.state.lanes.length - 1, origIdx + laneDelta))
        n.note = this.state.lanes[newIdx]
      }
      this.state.dirty = true
      this.render()
      return
    }

    // Cursor hints (no interaction)
    const edgeIdx = this.renderer.hitTestEdge(x, y, this.state, w, h)
    const noteIdx = this.renderer.hitTest(x, y, this.state, w, h)
    if (this.state.selectedIndices.size === 1 && edgeIdx === [...this.state.selectedIndices][0]) {
      this.canvasTarget.style.cursor = "ew-resize"
    } else if (noteIdx >= 0) {
      this.canvasTarget.style.cursor = "grab"
    } else {
      this.canvasTarget.style.cursor = "crosshair"
    }
  }

  private onMouseUp(_e: MouseEvent): void {
    if (this.interaction === "marquee") {
      this.renderer.marqueeRect = null
    }
    this.interaction = "none"
    this.dragNoteIdx = -1
    this.canvasTarget.style.cursor = "crosshair"
    this.render()
  }

  private onMouseLeave(): void {
    if (this.interaction === "pan" || this.interaction === "marquee") {
      this.interaction = "none"
      this.renderer.marqueeRect = null
      this.canvasTarget.style.cursor = "crosshair"
      this.render()
    }
  }

  private onWheel(e: WheelEvent): void {
    e.preventDefault()
    const { w } = this.canvasSize()
    const ppb = this.pxPerBeat(w)

    if (Math.abs(e.deltaX) > Math.abs(e.deltaY)) {
      this.renderer.setBeat(this.renderer.getBeat() - e.deltaX / ppb)
    } else {
      const newLA = this.renderer.getLookAhead() + (e.deltaY > 0 ? 2 : -2)
      this.renderer.setLookAhead(newLA)
      this.zoomSliderTarget.value = String(newLA)
    }
    this.render()
  }

  private onDoubleClick(e: MouseEvent): void {
    const { x, y } = this.canvasPos(e)
    const { w, h } = this.canvasSize()
    if (this.renderer.hitTest(x, y, this.state, w, h) >= 0) return

    const ppb = this.pxPerBeat(w)
    this.addNoteAt(this.renderer.xToBeat(x, this.playheadX(w), ppb), this.renderer.laneAtY(y, this.state, h))
  }

  // ── Keyboard ──

  private onKeyDown(e: KeyboardEvent): void {
    if ((e.target as HTMLElement).tagName === "INPUT" || (e.target as HTMLElement).tagName === "SELECT") return

    if (e.key === " ") {
      e.preventDefault()
      this.togglePlayback()
    } else if (e.key === "Delete" || e.key === "Backspace") {
      e.preventDefault()
      deleteSelected(this.state)
      this.updateNoteCount()
      this.render()
    } else if ((e.ctrlKey || e.metaKey) && e.key === "z") {
      e.preventDefault()
      undo(this.state)
      this.updateNoteCount()
      this.render()
    }
  }

  private onKeyUp(e: KeyboardEvent): void {
    // reserved for future use
  }

  // ── Note ops ──

  private addNoteAt(beat: number, lane: string): void {
    addNote(this.state, { beat: snapBeat(beat, this.state.snap), note: lane, duration: 1.0, velocity: 0.8 })
    this.state.selectedIndices.clear()
    this.state.selectedIndices.add(this.state.notes.length - 1)
    this.updateNoteCount()
    this.render()
  }

  // ── Playback ──

  togglePlayback(): void {
    if (this.playing) { this.stopPlayback() } else { this.startPlayback() }
  }

  private startPlayback(): void {
    this.audioCtx = new AudioContext()
    this.playing = true
    this.playStartBeat = this.renderer.getBeat()
    this.playStartWallTime = this.audioCtx.currentTime
    this.scheduledUntil = this.playStartBeat

    this.noteEvents = this.state.notes
      .map((n) => ({ beat: n.beat, endBeat: n.beat + n.duration, note: n.note, midi: this.mappingValue[n.note] ?? 60 }))
      .sort((a, b) => a.beat - b.beat)

    this.updatePlayBtn()
    this.scheduleLoop()
  }

  private stopPlayback(): void {
    this.playing = false
    if (this.rafId !== null) { cancelAnimationFrame(this.rafId); this.rafId = null }
    if (this.audioCtx) { this.audioCtx.close(); this.audioCtx = null }
    this.updatePlayBtn()
    this.render()
  }

  private scheduleLoop(): void {
    if (!this.playing || !this.audioCtx) return

    const elapsed = this.audioCtx.currentTime - this.playStartWallTime
    const currentBeat = this.playStartBeat + (elapsed * this.bpmValue) / 60
    const windowEnd = currentBeat + 0.5

    for (const ev of this.noteEvents) {
      if (ev.beat >= this.scheduledUntil && ev.beat < windowEnd) {
        this.playNoteSound(ev.midi, ev.beat)
      }
    }
    this.scheduledUntil = windowEnd
    this.renderer.setBeat(currentBeat)

    if (currentBeat > this.state.durationBeats + 1) { this.stopPlayback(); return }
    this.render()
    this.rafId = requestAnimationFrame(() => this.scheduleLoop())
  }

  private playNoteSound(midi: number, beat: number): void {
    if (!this.audioCtx) return
    const freq = 440 * Math.pow(2, (midi - 69) / 12)
    const beatDur = 60 / this.bpmValue
    const offset = (beat - this.renderer.getBeat()) * beatDur
    const time = this.audioCtx.currentTime + Math.max(0, offset)

    const osc = this.audioCtx.createOscillator()
    const gain = this.audioCtx.createGain()
    osc.type = this.instrumentValue === "drums" ? "square" : "sine"
    osc.frequency.value = this.instrumentValue === "drums" ? freq * 0.5 : freq
    gain.gain.setValueAtTime(0.15, time)
    gain.gain.exponentialRampToValueAtTime(0.001, time + beatDur * 0.8)
    osc.connect(gain)
    gain.connect(this.audioCtx.destination)
    osc.start(time)
    osc.stop(time + beatDur * 0.8)
  }

  private updatePlayBtn(): void {
    if (!this.playBtnTarget) return
    this.playBtnTarget.textContent = this.playing ? "Stop" : "Play"
    this.playBtnTarget.className = this.playing
      ? "bg-red/10 border border-red/30 text-red px-4 py-1.5 rounded-soft text-[12px] font-medium transition-colors cursor-pointer"
      : "bg-green/10 border border-green/30 text-green px-4 py-1.5 rounded-soft text-[12px] font-medium transition-colors cursor-pointer"
  }

  // ── Stimulus actions ──

  setSnap(e: Event): void {
    this.state.snap = parseFloat((e.target as HTMLSelectElement).value) as 0 | 0.25 | 0.125
  }

  setZoom(e: Event): void {
    this.renderer.setLookAhead(parseInt((e.target as HTMLInputElement).value, 10))
    this.render()
  }

  deleteSelected(): void {
    deleteSelected(this.state)
    this.updateNoteCount()
    this.render()
  }

  undoAction(): void {
    undo(this.state)
    this.updateNoteCount()
    this.render()
  }

  async save(): Promise<void> {
    if (!this.state.dirty) return
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute("content") ?? ""
    try {
      const resp = await fetch(`/x42/lessons/${this.lessonIdValue}/update_timing`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", "X-CSRF-Token": csrfToken, Accept: "application/json" },
        body: JSON.stringify({ track_id: this.trackIdValue, notes_data: this.state.notes }),
      })
      if (resp.ok) {
        this.state.dirty = false
        this.unsavedIndicatorTarget.style.display = "none"
        this.showNotice("Saved")
      } else {
        this.showAlert("Save failed")
      }
    } catch {
      this.showAlert("Network error")
    }
  }

  switchTrack(e: Event): void {
    const trackId = (e.target as HTMLSelectElement).value
    if (trackId) window.location.href = `/x42/lessons/${this.lessonIdValue}/timing_editor?track_id=${trackId}`
  }

  private updateNoteCount(): void {
    const n = this.state.selectedIndices.size
    const total = this.state.notes.length
    this.noteCountTarget.textContent = n > 0 ? `${n} selected / ${total} total` : `${total} notes`
    if (this.state.dirty) this.unsavedIndicatorTarget.style.display = "inline"
  }

  private render(): void {
    this.renderer.render(this.state)
    this.updateNoteCount()
  }

  private showNotice(msg: string): void {
    const el = document.createElement("div")
    el.className = "fixed bottom-4 right-4 bg-green/10 border border-green/30 text-green px-4 py-2 rounded-soft text-[13px] z-50"
    el.textContent = msg
    document.body.appendChild(el)
    setTimeout(() => el.remove(), 2000)
  }

  private showAlert(msg: string): void {
    const el = document.createElement("div")
    el.className = "fixed bottom-4 right-4 bg-red/10 border border-red/30 text-red px-4 py-2 rounded-soft text-[13px] z-50"
    el.textContent = msg
    document.body.appendChild(el)
    setTimeout(() => el.remove(), 3000)
  }
}
